// =============================================================================
// staticSite/menuAiProposer — grupowanie kategorii w 4-7 grup menu przez AI
// (prompt `static_menu_grouping` w thematic_prompts). Wejscie: kategorie z modelu,
// macro mapy, podpowiedz z seedow (seed <-> kategorie WP), wskazowka uzytkownika.
// Wyjscie normalizowane w kodzie: nieprzypisane -> „Pozostałe", duplikaty
// usuwane, autor/kontakt dopina kod (autor do main albo footer z theme_seed),
// wynik przez validateMenu. Wzorzec 1:1 z designBriefGenerator.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import type { SiteMenu, SiteModel, StaticSiteRow } from "./types";
import { buildMenuFromGroups, sortedCategories, validateMenu } from "./menuBuilder";

const PREFIX = "STATIC";
const FALLBACK_PROMPT = `Pogrupuj kategorie strony {{site_name}} w 4–7 grup menu. Kategorie: {{categories}}. Zwróć TYLKO JSON {"groups":[{"label":"…","category_slugs":["…"]}]}.`;

type MenuScope = Pick<SiteModel, "categories" | "pages" | "author" | "domain">;

interface MenuProposalInput {
  site: StaticSiteRow;
  scope: MenuScope;
  hint?: string;
}

interface MenuProposalResult {
  menu: SiteMenu;
  costUsd: number;
  model: string;
}

async function collectContext(site: StaticSiteRow, scope: MenuScope, hint: string) {
  const [mapRes, seedsRes, linksRes] = await Promise.all([
    (supabase as any).from("thematic_maps").select("name, macro_statement, language").eq("id", site.map_id).maybeSingle(),
    (supabase as any).from("thematic_seeds").select("id, name, status").eq("map_id", site.map_id).limit(60),
    (supabase as any).from("thematic_seed_wp_categories").select("seed_id, wp_category_slug").limit(200),
  ]);
  const map = mapRes?.data || {};
  const seeds = ((seedsRes?.data || []) as Array<{ id: string; name: string; status: string }>).filter((s) => s.status !== "rejected");
  const links = (linksRes?.data || []) as Array<{ seed_id: string; wp_category_slug: string | null }>;
  const seedGroups = seeds
    .map((s) => ({ name: s.name, slugs: links.filter((l) => l.seed_id === s.id && l.wp_category_slug).map((l) => l.wp_category_slug as string) }))
    .filter((g) => g.slugs.length)
    .map((g) => `${g.name}: ${g.slugs.join(", ")}`)
    .join("\n");
  const cats = sortedCategories(scope);
  return {
    site_name: (map.name as string) || site.domain,
    domain: site.domain,
    macro_statement: (map.macro_statement as string) || "",
    language: (map.language as string) || "pl",
    categories: cats.map((c) => `${c.slug} | ${c.name} | ${(c.description || "").slice(0, 120)} | ${c.count}`).join("\n"),
    category_count: String(cats.length),
    seed_groups: seedGroups || "brak",
    hint: hint.trim() || "brak",
  };
}

export async function proposeMenuGroups(input: MenuProposalInput): Promise<MenuProposalResult> {
  const { site, scope } = input;
  if (!scope.categories.length) throw new Error("Brak kategorii do pogrupowania — uruchom analizę.");
  const vars = await collectContext(site, scope, input.hint || "");
  const promptDb = await loadPrompt("static_menu_grouping" as any);
  if (!promptDb) throw new Error("Brak promptu 'static_menu_grouping' w bazie (Ustawienia → Prompty AI).");
  const compiled = compilePromptSafe(promptDb.template, vars, "static_menu_grouping", FALLBACK_PROMPT);
  const provider = getLLMProvider((promptDb.provider || "claude") as "openai" | "claude");
  const model = promptDb.modelId || promptDb.model || "claude-sonnet-5";
  logPublic(PREFIX, `Grupowanie menu ${site.domain}: ${scope.categories.length} kategorii, ${model}`);
  try {
    const res = await provider.call(compiled, { model, step: "static_menu_grouping", mapId: site.map_id, temperature: promptDb.temperature ?? undefined, maxTokens: 1500 });
    const parsed = parseLLMJSON<{ groups?: Array<{ label?: unknown; category_slugs?: unknown }> }>(res.text);
    const raw = buildMenuFromGroups(parsed?.groups || [], scope, site.theme_seed);
    const validated = validateMenu(raw, scope);
    if (!validated) throw new Error("AI zwróciło puste menu");
    logPublic(PREFIX, `Menu AI: ${validated.menu.main.filter((i) => i.type === "group").length} grup, $${(res.costUsd ?? 0).toFixed(3)}`);
    return { menu: { ...validated.menu, source: "ai" }, costUsd: res.costUsd ?? 0, model: res.modelUsed || model };
  } catch (e) {
    logError(PREFIX, "Grupowanie menu nieudane", e);
    throw e;
  }
}
